import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const MobileApp = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What the mobile app is for</Typography>
      <Typography>
        The AmbuNet mobile app (iOS and Android) has its own PTS screen for
        whoever is actually driving a duty — a phone-native equivalent of the{' '}
        <DocLink url="/patient-transport/crew-view" text="Crew View" docsPage />{' '}
        web screen, covering today's duty, working through a journey, and
        staying in touch with control room. It's a separate app from the
        browser, but talks to the same PTS data — a duty updated from Control
        or the web crew view is reflected here too.
      </Typography>
      <Alert severity="info" variant="outlined">
        No PTS permission is needed — like the web crew view, the PTS screen
        is open to every employee when Patient Transport is on the
        subscription (an enterprise module). Only the co-ordination screens
        need the <strong>Co-Ordinator</strong> permission.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Today's duty and journeys</Typography>
      <Typography>
        Opening the Patient Transport tab shows the assigned vehicle, shift
        time, how many journeys are still open, and overall progress for the
        duty. Below that, the current journey is shown in full — patient,
        pickup, dropoff, and timing — with the next journey queued
        underneath.
      </Typography>
      <Typography>
        Patient risk flags, clinical notes, and mobility category are shown
        on the current journey card, the same information visible on the web
        crew view.
      </Typography>
      <Typography>
        Below the active duty, <strong>My Duties</strong> lists current and
        upcoming PTS duties with accept/decline for shifts awaiting
        acknowledgement. Each duty expands to its planned journeys, and
        tapping a journey opens the full read-only details — both stops with
        their notes and escorts, mobility requirements, risk flags,
        allergies, medications and history, the booking notes, and the
        patient's risk assessments — the same tap-through the web crew view
        has.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Working a journey</Typography>
      <Typography>
        The same single action button drives the journey through its stages:
        Acknowledge → Travel to pickup → Arrived at pickup → Patient onboard →
        Travel to destination → Arrived at destination → Complete handover →
        Complete journey. The button label always reflects whichever step
        comes next. Journeys dropping the patient at their own home skip the
        handover step — after arrival, the button goes straight to{' '}
        <strong>Complete journey</strong>.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Marking arrival at pickup or destination checks the device's current
        location against the journey's address. If they don't line up
        (roughly 500m or more apart), a confirmation prompt appears before the
        status is saved — this can be confirmed anyway if location isn't
        available or accurate. This needs the app's location permission to be
        allowed.
      </Alert>
      <Typography>
        Where a journey has a next stop, an <strong>Open sat nav</strong>{' '}
        button hands off to the device's own navigation app (Apple Maps,
        Google Maps, or Waze) for the actual turn-by-turn driving directions —
        the AmbuNet app itself does not provide turn-by-turn navigation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Adding your own jobs (dedicated duties)
      </Typography>
      <Typography>
        On a dedicated duty set up with <strong>No dispatch — the crew add
        their own jobs</strong>, the clocked-in crew get an{' '}
        <strong>Add a job</strong> button on the duty screen, with the same
        form as the web crew view: job type, priority, requesting ward,
        patient lookup or create (details can follow), home address lookup,
        mobility, and the choice to add the job to an existing run or start a
        new one. See{' '}
        <DocLink
          url="/patient-transport/crew-view"
          text="Crew View"
          docsPage
        />{' '}
        for the full walkthrough — everything there applies on mobile too.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Staying in touch with control</Typography>
      <Typography>
        The chat button opens a direct conversation with the dispatcher for
        that duty, with unread messages badged on the button.
      </Typography>
      <Alert severity="info" variant="outlined">
        Unlike the web crew view, the mobile app does not currently send a
        push notification when control room changes, retimes, or cancels a
        journey — the app needs to be open to see updates, which happen
        automatically in the background while it's on screen. When something
        does change, a dismissible banner appears at the top of the duty
        screen describing what changed.
      </Alert>
      <Alert severity="warning" variant="outlined">
        <strong>Report a delay</strong> is only available on the web crew
        view, not in the mobile app yet. To log a delay with a reason code,
        use the browser crew view — or tell control room over duty chat so a
        dispatcher can act on it.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Location sharing</Typography>
      <Typography>
        Location sharing is consent-based. When dispatch asks to see the
        vehicle's live position, the crew member gets a push notification
        ("Location sharing requested") and a card at the top of the PTS duty
        screen — <strong>Share your live location?</strong> — with{' '}
        <strong>Share location</strong> and <strong>No, don't share</strong>{' '}
        buttons. Nothing is ever shared until this is approved; clocking in
        on its own never starts sharing.
      </Typography>
      <Typography>
        Once approved (and while clocked into an active PTS shift), the app
        periodically shares the device's location in the background so the
        vehicle appears live on the dispatch map, until the end of the shift
        or clock-out, whichever comes first. This needs the app's location
        permission to be allowed — a status chip on the resource card shows
        whether location is live, starting, blocked, or off.
      </Typography>
      <Alert severity="info" variant="outlined">
        Declining never blocks any other part of the crew workflow — journeys
        can still be worked as normal. Dispatch can always send another
        request, which brings the card back. The mobile app is the only place
        this request can be answered; the web crew view has no consent
        prompt.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Clocking out with open journeys</Typography>
      <Typography>
        The mobile dashboard blocks clock-out while the duty still has open
        journeys — a "PTS journeys open" row explains how many journeys must
        be completed before clocking out. Finish (or have control room
        reassign/cancel) the remaining journeys first.
      </Typography>
    </Stack>
  </Stack>
)

export default MobileApp
